import React, { useMemo, useState } from 'react';
import {
  PanResponder,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { Booking } from '../../data/bookings';
import useJobProgress from '../../hooks/useJobProgress';
import {
  colors,
  MAX_CONTENT_WIDTH,
  radii,
  screenPadding,
  spacing,
} from '../../theme';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

type CardProps = {
  /** The one booking this card is about. */
  booking: Booking;
  /** Opens the booking's own screen, where everything else about it lives. */
  onPress: () => void;
  /** Sends the stars straight from the bar. Omit and no stars are drawn. */
  onRate?: (stars: number) => void;
  /** Puts the rating prompt away for this session. */
  onDismiss?: () => void;
  /** Fixed width when the cards are a swipeable row; unset when there is one. */
  width?: number;
};

type Props = {
  /**
   * Every booking worth a bar, most advanced first - see HomeScreen.
   *
   * A household can have three jobs in one morning, and a bar that showed only
   * the furthest along would hide the code for the one whose professional is
   * at the door right now.
   */
  bookings: Booking[];
  onPress: (booking: Booking) => void;
  onRate?: (booking: Booking, stars: number) => void;
  onDismiss?: (booking: Booking) => void;
};

/**
 * The job that is happening right now, over the Home screen.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS A BAR AND NOT A CARD IN THE PAGE
 * ---------------------------------------------------------------------------
 * The two things a customer needs while a job runs - is anybody coming, and
 * what is the code - were reachable only by opening the Bookings tab and then
 * the booking. That is two taps away from a screen they are already on, at the
 * exact moment somebody is standing at their door asking for a number.
 *
 * So it follows the page rather than sitting in it, in the same strip the cart
 * bar uses and directly above it. A card in the scroll would be correct for
 * about four seconds and then be somewhere above the fold.
 *
 * ---------------------------------------------------------------------------
 * ONE CODE AT A TIME, AND THAT IS THE SERVER'S RULE
 * ---------------------------------------------------------------------------
 * The start code exists only while the professional is `arrived`, the end code
 * only while the job is `started`, and each is spent when it is read out.
 * Neither is a thing the customer holds in advance, so this shows whichever
 * one is live and says what it is for. Both at once is not a layout this app
 * can draw - the server does not send them together, deliberately.
 *
 * ---------------------------------------------------------------------------
 * AND IT ASKS FOR THE RATING
 * ---------------------------------------------------------------------------
 * When the job finishes, the same bar becomes the rating prompt rather than
 * vanishing: the moment a customer has an opinion is the moment the work
 * ended, and a review screen two taps into the Bookings tab collects that
 * opinion from almost nobody. Five taps here, or open it to write something.
 */
/**
 * How far up a finger has to travel before the bar takes the gesture from the
 * pager, and before it counts as "open this".
 *
 * Claiming at 8pt and opening at 40 leaves room between the two: a drag can be
 * claimed, thought better of, and released without opening anything.
 */
const CLAIM_DY = 8;
const OPEN_DY = 40;
/** How much more vertical than horizontal a drag must be to be ours. */
const VERTICAL_BIAS = 1.5;

/**
 * The sweep of the bar's top edge.
 *
 * Deliberately far wider than the card radius the rest of the app uses - at
 * `radii.md` the top read as a corner, and the point here is a whole edge that
 * rises in the middle.
 */
const TOP_CURVE = 28;

/**
 * Whether this drag is the bar's to take.
 *
 * Exported, and pure, because it is the only part of the gesture worth
 * testing and the only part that can be got wrong quietly: `PanResponder`
 * hands its callbacks its OWN gesture state, so a test that calls the
 * handlers with made-up numbers proves nothing about either of these rules.
 */
export const claimsDrag = (dx: number, dy: number): boolean =>
  dy < -CLAIM_DY && Math.abs(dy) > Math.abs(dx) * VERTICAL_BIAS;

/** Whether letting go here opens the booking. */
export const opensOnRelease = (dy: number): boolean => dy < -OPEN_DY;

function BookingBar({ booking, onPress, onRate, onDismiss, width }: CardProps) {
  const rating = booking.status === 'completed';

  /* Only a running job has a clock. `useJobProgress` returns null otherwise
     and schedules no interval - see its own note. */
  const progress = useJobProgress(
    booking.startedAt,
    booking.duration,
    !rating && booking.stage === 'service',
  );

  /*
   * Flick it up to open the booking.
   *
   * A tap already does this and keeps doing it; this is the gesture the bar's
   * shape suggests - a sheet peeking from the bottom edge asks to be pulled
   * up, and people were trying it.
   *
   * `PanResponder` rather than a library: this is one gesture on one view,
   * and the app carries no gesture handler.
   *
   * The claim is deliberately narrow. The bar lives inside a horizontal pager
   * (see `LiveBookingBar` below), so a responder that took every drag would
   * eat the left-right swipe between jobs. It only claims a drag that is
   * clearly UP and clearly more vertical than horizontal, and leaves
   * everything else to the pager and the vertical scroll underneath.
   */
  const pan = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: (_event, { dx, dy }) =>
          claimsDrag(dx, dy),
        /* Short of the claim and it is not a flick, it is a wobble on the way
           to a tap - which the Pressable underneath still handles. */
        onPanResponderRelease: (_event, { dy }) => {
          if (opensOnRelease(dy)) {
            onPress();
          }
        },
      }),
    [onPress],
  );

  const code = booking.endOtp ?? booking.otp;
  const codeLabel = booking.endOtp ? 'Code to finish' : 'Code to start';

  /* The server's own words for where this is, so the bar and the booking
     screen cannot describe the same state differently. */
  const headline = rating
    ? 'How was the service?'
    : (booking.progress ?? 'Booking in progress');

  /* The clock while one is running, else when the job is due. */
  const aside = rating
    ? null
    : (progress?.countdown ?? progress?.elapsed ?? booking.when);

  return (
    <View style={[styles.wrap, width ? { width } : null]} pointerEvents="box-none">
      <View style={styles.frame} {...pan.panHandlers}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            rating
              ? `Rate ${booking.title}`
              : `${headline}. ${booking.title}. Open booking`
          }
          onPress={onPress}
          style={({ pressed }) => [styles.bar, pressed ? styles.pressed : null]}
        >
          {/*
            The one hint that the bar opens.

            A tap opened it and still does, and a swipe up now does too - but
            a gesture nobody is told about is a gesture nobody uses. This is
            deliberately the smallest signal that works: no words, so nothing
            to translate, and no animation to explain itself.

            Centred along the top edge, where a sheet's grab handle sits, so
            it reads as "this whole thing lifts" rather than as a control on
            the end of the headline.

            Not drawn in rating mode: the dismiss cross is in that corner
            there, and the bar is asking for stars rather than offering to
            open.
          */}
          {rating ? null : (
            <View style={styles.hint}>
              <Icon name="pullUp" size={16} color={colors.inkMuted} />
            </View>
          )}

          <View style={styles.top}>
            <View style={styles.dot} />
            <Text variant="labelSm" color={colors.ink} style={styles.headline}>
              {headline}
            </Text>
            {aside ? (
              <Text variant="caption" color={colors.inkMuted}>
                {aside}
              </Text>
            ) : null}
          </View>

          <Text
            variant="caption"
            color={colors.inkMuted}
            numberOfLines={1}
          >
            {booking.title}
            {booking.professional ? ` · ${booking.professional.name}` : ''}
          </Text>

          {rating && onRate ? (
            <View style={styles.stars}>
              {[1, 2, 3, 4, 5].map(star => (
                <Pressable
                  key={star}
                  accessibilityRole="button"
                  accessibilityLabel={`${star} star${star > 1 ? 's' : ''}`}
                  hitSlop={6}
                  /* Stops the press bubbling into the bar, which would open
                     the booking instead of recording the star. */
                  onPress={() => onRate(star)}
                  style={({ pressed }) => (pressed ? styles.pressed : null)}
                >
                  <Icon name="star" size={26} color={colors.rating} />
                </Pressable>
              ))}
            </View>
          ) : code ? (
            <View style={styles.codeRow}>
              <Text variant="caption" color={colors.inkMuted}>
                {codeLabel}
              </Text>
              <View style={styles.digits}>
                {code.split('').map((digit, index) => (
                  <View key={`${digit}-${index}`} style={styles.digit}>
                    <Text variant="labelSm" color={colors.onPrimary}>
                      {digit}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          ) : !rating ? (
            /*
             * The waiting state, and it is here to keep the bar ONE height.
             *
             * Without it the bar grew by a row the moment a code landed -
             * "Finding a professional" sat a code-row shorter than
             * "Professional assigned", so the whole thing jumped under the
             * customer's thumb at the one moment they were watching it.
             * The row is now always drawn; only what is in it changes.
             */
            <View style={styles.codeRow}>
              <Text variant="caption" color={colors.inkMuted}>
                Your code will appear here
              </Text>
            </View>
          ) : null}
        </Pressable>

        {/* A sibling of the bar, like the cart bar's cross: nested, a tap
            would dismiss AND open the booking as it bubbled. */}
        {rating && onDismiss ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Hide the rating prompt"
            onPress={onDismiss}
            hitSlop={10}
            style={({ pressed }) => [
              styles.dismiss,
              pressed ? styles.pressed : null,
            ]}
          >
            <Icon
              name="xCircle"
              size={18}
              color={colors.inkMuted}
              strokeWidth={2}
            />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

/**
 * One bar, or a swipeable row of them.
 *
 * ---------------------------------------------------------------------------
 * WHY IT SCROLLS RATHER THAN PICKING A WINNER
 * ---------------------------------------------------------------------------
 * A household books three services for one morning and gets three bookings,
 * each with its own professional and its own code. Showing only the furthest
 * along is the obvious simplification and the wrong one: the job that needs a
 * code read out is not always the job that started first, and a customer
 * cannot reach the other two from a bar that has decided for them.
 *
 * So every live booking is a card and the row scrolls. With one there is
 * nothing to scroll and no dots are drawn - the commonest case must not pay
 * for the rarer one.
 */
function LiveBookingBar({ bookings, onPress, onRate, onDismiss }: Props) {
  const { width: screen } = useWindowDimensions();
  const [index, setIndex] = useState(0);

  if (bookings.length === 0) {
    return null;
  }

  if (bookings.length === 1) {
    const only = bookings[0];
    return (
      <BookingBar
        booking={only}
        onPress={() => onPress(only)}
        onRate={onRate ? stars => onRate(only, stars) : undefined}
        onDismiss={onDismiss ? () => onDismiss(only) : undefined}
      />
    );
  }

  /* Held to the same column the single bar uses, so one card and three cards
     line up over the tab bar identically. */
  const card = Math.min(screen, MAX_CONTENT_WIDTH + screenPadding * 2);

  return (
    <View pointerEvents="box-none">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        /* Snapped, not free: a card half off the edge reads as a rendering
           fault rather than as "there is another one". */
        snapToInterval={card}
        snapToAlignment="start"
        decelerationRate="fast"
        onMomentumScrollEnd={event =>
          setIndex(Math.round(event.nativeEvent.contentOffset.x / card))
        }
      >
        {bookings.map(item => (
          <BookingBar
            key={item.id}
            booking={item}
            width={card}
            onPress={() => onPress(item)}
            onRate={onRate ? stars => onRate(item, stars) : undefined}
            onDismiss={onDismiss ? () => onDismiss(item) : undefined}
          />
        ))}
      </ScrollView>

      {/* The only thing that says there is more than one. Without it a second
          card is a secret. */}
      <View style={styles.dots} pointerEvents="none">
        {bookings.map((item, position) => (
          <View
            key={item.id}
            style={[styles.dotMark, position === index ? styles.dotOn : null]}
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  /* The cart bar's own frame, so the two stack as one column rather than as
     two differently sized slabs. */
  wrap: {
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH + screenPadding * 2,
    alignSelf: 'center',
    paddingHorizontal: screenPadding,
    paddingBottom: spacing.sm,
  },
  /* The bar's own frame. It carries no shadow: the bar is a dark slab on a
     light canvas and already reads as lifted, and the drop shadow under it
     only showed up as a grey smudge against the cart bar directly below -
     two stacked shadows where the eye expects one column.

     It stays as a wrapper because the dismiss cross is positioned against
     it rather than against the pressable it would otherwise swallow taps
     from. */
  frame: {
    borderRadius: radii.md,
    borderTopLeftRadius: TOP_CURVE,
    borderTopRightRadius: TOP_CURVE,
  },
  /*
   * Light green, not the cart bar's near-black.
   *
   * The two sit in one column and used to be the same dark slab, so a live
   * job and an unpaid basket read as one object split in half. They are
   * different things - one is work already paid for and under way, the other
   * is a basket nobody has bought - and the lighter ground says so before a
   * word is read.
   *
   * `surfaceAccent` is one step off the canvas, which is not enough on its
   * own, so the border does the lifting the dark fill used to do.
   */
  bar: {
    gap: spacing.xs,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: radii.md,
    /*
     * The top edge is a curve, not a corner.
     *
     * A square-ish top edge reads as a panel that is simply there. The wide
     * arc gives the bar a raised middle that falls away to the sides, which
     * is the shape of something lifting from the bottom of the screen - and
     * it is what makes the swipe legible before the mark in the middle of it
     * is even noticed.
     *
     * Only the top. The bottom stays at the card radius because it sits
     * against the cart bar and the tab bar below, and a curve there would
     * leave two wedges of canvas showing between them.
     */
    borderTopLeftRadius: TOP_CURVE,
    borderTopRightRadius: TOP_CURVE,
    backgroundColor: colors.surfaceAccent,
    borderWidth: 1,
    borderColor: colors.borderStrong,
  },
  /* Tight against the bar's top padding: the arc is a whisper, and a full
     `gap` under it made it look like a row of its own. */
  hint: {
    alignItems: 'center',
    marginTop: -spacing.xs,
    marginBottom: -spacing.xs,
  },
  top: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  headline: {
    flex: 1,
  },
  /* The one thing on the bar that says "now" rather than "today". */
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary,
  },
  codeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.xs,
    /* A digit box is its 17pt line plus 2pt of padding top and bottom. Held
       here so the waiting line, which has no boxes in it, reserves exactly
       the room the code will take and the bar keeps its height. */
    minHeight: 21,
  },
  digits: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  /* Boxed digits, the same shape the booking screen draws them in - a code
     read off a phone is read one character at a time. Dark on the light bar,
     which makes them the one thing on it that carries weight. */
  digit: {
    minWidth: 24,
    paddingVertical: 2,
    borderRadius: radii.sm,
    alignItems: 'center',
    backgroundColor: colors.primary,
  },
  stars: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  dismiss: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: spacing.sm,
  },
  dots: {
    flexDirection: 'row',
    alignSelf: 'center',
    gap: spacing.xs,
    paddingBottom: spacing.sm,
  },
  dotMark: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.borderStrong,
  },
  dotOn: {
    backgroundColor: colors.primary,
  },
  pressed: {
    opacity: 0.8,
  },
});

export default React.memo(LiveBookingBar);
